import React, { useState, useMemo, useCallback } from 'react';
import { Modal, Button, Typography, Tabs } from 'antd';
// Types
import { GetCampaignOffers_getCampaignOffers_entities } from 'api/campaignV2/types/GetCampaignOffers';
import { CbDeliverableV2Status } from 'api/graphql-global-types';
// Components
import TimelineDeliverablesReviewForm from '../CampaignDeliverablesSubmissionReview/components/TimelineDeliverablesReviewForm/TimelineDeliverablesReviewForm';
// Styles
import styles from './TimelineDeliverablesReviewModal.module.scss';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

type TimelineDeliverablesReviewModalProps = {
  offer: GetCampaignOffers_getCampaignOffers_entities;
  onClose: () => void;
  onFinish: () => void;
  refetch?: () => void;
};

const TimelineDeliverablesReviewModal = ({
  offer,
  onClose,
  onFinish,
  refetch,
}: TimelineDeliverablesReviewModalProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<string>('0');

  // Get all deliverables that are submitted for review
  const deliverablesForReview = useMemo(() => {
    return (
      offer.deliverablesV2?.filter(
        (deliverable) =>
          deliverable.status === CbDeliverableV2Status.ON_BRAND_REVIEW
      ) || []
    );
  }, [offer.deliverablesV2]);

  const handleDeliverableReviewComplete = useCallback(async () => {
    if (refetch) {
      await refetch();
    }

    // If no more deliverables to review, close the modal
    if (deliverablesForReview.length <= 1) {
      onFinish();
    }
  }, [refetch, deliverablesForReview.length, onFinish]);

  const handleCustomClose = useCallback(() => {
    // Do nothing - we want to keep the modal open
  }, []);

  const hasOne = deliverablesForReview.length === 1;
  const hasMultiple = deliverablesForReview.length > 1;

  return (
    <Modal
      title="Review Deliverables"
      visible={!!offer}
      footer={null}
      onCancel={onClose}
      width="1200px"
      className={styles.modal}
    >
      <div className={styles.root}>
        {hasMultiple && (
          <div className={styles.header}>
            <Title level={4}>Multiple Items for Review</Title>
            <Text type="secondary">
              Review all submitted deliverables for this talent
            </Text>
          </div>
        )}
        {deliverablesForReview.length === 0 ? (
          <div className={styles.noItems}>
            <Text>No items are currently submitted for review.</Text>
            <Button type="primary" onClick={onFinish} style={{ marginTop: 16 }}>
              Close
            </Button>
          </div>
        ) : hasOne ? (
          // Single item - show directly without tabs
          <TimelineDeliverablesReviewForm
            deliverable={deliverablesForReview[0]}
            offer={offer}
            onClose={handleCustomClose}
            refetchOffers={handleDeliverableReviewComplete}
          />
        ) : (
          // Multiple items - show in tabs
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className={styles.tabs}
          >
            {deliverablesForReview.map((deliverable, index) => (
              <TabPane
                tab={
                  <span>
                    {deliverable.task?.title || `Deliverable ${index + 1}`}
                  </span>
                }
                key={index.toString()}
              >
                <TimelineDeliverablesReviewForm
                  deliverable={deliverable}
                  offer={offer}
                  onClose={handleCustomClose}
                  refetchOffers={handleDeliverableReviewComplete}
                />
              </TabPane>
            ))}
          </Tabs>
        )}
      </div>
    </Modal>
  );
};

export default TimelineDeliverablesReviewModal;
